import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiBookOpen, FiPlus, FiCalendar, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import AddCourseModal from './AddCourseModal';
import EditCourseModal from './EditCourseModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import AssignStudentModal from './AssignStudentModal';
import BulkAssignModal from './BulkAssignModal';
import RemoveStudentModal from './RemoveStudentModal';
import CourseVisibilityManager from './CourseVisibilityManager';
import StudentVisibilityManager from './StudentVisibilityManager';
import Notification from '../common/Notification';
import styles from './CourseManagement.module.css';

const MENU_WIDTH = 280;
const DESCRIPTION_MIN_LENGTH = 100;

const filterMeta = [
  { key: 'all', label: 'All Courses' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

const parseDateOnly = (dateString) => {
  if (!dateString) return null;
  if (dateString instanceof Date) {
    return {
      year: dateString.getUTCFullYear(),
      month: dateString.getUTCMonth(),
      day: dateString.getUTCDate(),
    };
  }
  const normalized = typeof dateString === 'string' ? dateString.split('T')[0] : String(dateString);
  const parts = normalized.split('-');
  if (parts.length === 3) {
    return {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10) - 1,
      day: parseInt(parts[2], 10),
    };
  }
  const parsed = new Date(dateString);
  return {
    year: parsed.getUTCFullYear(),
    month: parsed.getUTCMonth(),
    day: parsed.getUTCDate(),
  };
};

const formatCourseDate = (course) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const start = parseDateOnly(course.start_date);
  if (!start) return 'Dates TBD';
  const startLabel = `${monthNames[start.month]} ${start.day}${start.year !== now.getFullYear() ? `, ${start.year}` : ''}`;
  let endLabel = '';
  let hasEnded = false;
  if (course.end_date) {
    const end = parseDateOnly(course.end_date);
    if (end) {
      endLabel = `${monthNames[end.month]} ${end.day}${end.year !== now.getFullYear() ? `, ${end.year}` : ''}`;
      const endDate = new Date(end.year, end.month, end.day);
      hasEnded = endDate < today;
    }
  }
  if (hasEnded && endLabel) {
    return `Started ${startLabel} · Ended ${endLabel}`;
  }

  return `Started ${startLabel}${endLabel ? ` · Ends ${endLabel}` : ''}`;
};

const getCourseStatus = (course) => {
  if (!course.start_date) return 'Not Started';
  const now = new Date();
  const start = new Date(course.start_date);
  const end = course.end_date ? new Date(course.end_date) : null;
  if (end && now > end) return 'Ended';
  if (now >= start) return course.is_active ? 'Active' : 'Inactive';
  return 'Not Started';
};

function CourseManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Default to 'active' filter for instructors, 'all' for admins
  const [activeFilter, setActiveFilter] = useState(user?.role === 'instructor' ? 'active' : 'all');
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [activeCourseForMenu, setActiveCourseForMenu] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuRef = useRef(null);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [showStudentVisibilityModal, setShowStudentVisibilityModal] = useState(false);

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCourses();
      setCourses(response.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Set default filter to 'active' for instructors when user loads (only once)
  useEffect(() => {
    if (user?.role === 'instructor' && activeFilter === 'all') {
      setActiveFilter('active');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const filterCounts = useMemo(() => {
    const counts = { all: courses.length, active: 0, inactive: 0 };
    courses.forEach(course => {
      if (course.is_active) {
        counts.active += 1;
      } else {
        counts.inactive += 1;
      }
    });
    return counts;
  }, [courses]);

  const filteredCourses = useMemo(() => {
    return courses.filter(course => {
      const instructorName = `${course.instructor_first_name || ''} ${course.instructor_last_name || ''}`.trim();
      const query = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !query ||
        course.code.toLowerCase().includes(query) ||
        course.name.toLowerCase().includes(query) ||
        instructorName.toLowerCase().includes(query);
      const matchesFilter =
        activeFilter === 'all' ||
        (activeFilter === 'active' && course.is_active) ||
        (activeFilter === 'inactive' && !course.is_active);
      return matchesSearch && matchesFilter;
    });
  }, [courses, searchTerm, activeFilter]);

  const expandDescription = useCallback((courseId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  }, []);

  const handleManageClick = (event, course) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const viewportWidth = window.innerWidth;
    const maxLeft = viewportWidth - MENU_WIDTH - 16;
    const left = Math.min(Math.max(16, rect.left + scrollX), Math.max(16, maxLeft));
    setMenuAnchor({
      top: rect.bottom + scrollY + 8,
      left,
    });
    setActiveCourseForMenu(course);
  };

  const closeMenu = () => {
    setActiveCourseForMenu(null);
    setMenuAnchor(null);
  };

  const handleMenuSelection = (action) => {
    action();
    closeMenu();
  };

  useEffect(() => {
    if (!activeCourseForMenu) return undefined;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeCourseForMenu]);

  useEffect(() => {
    if (!menuAnchor || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 16;
    let nextTop = menuAnchor.top;
    let nextLeft = menuAnchor.left;

    if (rect.right > viewportWidth - padding) {
      nextLeft = Math.max(padding, viewportWidth - rect.width - padding);
    }
    if (rect.bottom > viewportHeight - padding) {
      nextTop = Math.max(padding, viewportHeight - rect.height - padding);
    }

    if (nextTop !== menuAnchor.top || nextLeft !== menuAnchor.left) {
      setMenuAnchor({ top: nextTop, left: nextLeft });
    }
  }, [menuAnchor]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleAddCourse = () => setShowAddModal(true);
  const handleEditCourse = (course) => {
    setSelectedCourse(course);
    setShowEditModal(true);
  };
  const handleDeleteCourse = (course) => {
    setSelectedCourse(course);
    setShowDeleteModal(true);
  };
  const handleViewStudents = useCallback((course) => navigate(`/admin/courses/${course.id}/students`), [navigate]);
  const handleAssignStudent = (course) => {
    setSelectedCourse(course);
    setShowAssignModal(true);
  };
  const handleRemoveStudent = (course) => {
    setSelectedCourse(course);
    setShowRemoveModal(true);
  };
  const handleBulkAssign = (course) => {
    setSelectedCourse(course);
    setShowBulkAssignModal(true);
  };
  const handleManageContent = useCallback((course) => navigate(`/courses/${course.id}?manageContent=true`), [navigate]);
  const handleVisibilitySettings = useCallback((course) => {
    setSelectedCourse(course);
    setShowVisibilityModal(true);
  }, []);

  const handleStudentVisibilitySettings = useCallback((course) => {
    setSelectedCourse(course);
    setShowStudentVisibilityModal(true);
  }, []);

  const handleCourseDeleted = () => {
    setCourses(prev => prev.filter(c => c.id !== selectedCourse?.id));
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Course Deleted',
      message: `${selectedCourse?.code} has been removed.`,
    });
  };

  const confirmDeleteCourse = async () => {
    if (!selectedCourse) return;
    setDeleteLoading(true);
    try {
      await apiService.deleteCourse(selectedCourse.id);
      handleCourseDeleted();
    } catch (error) {
      console.error('Error deleting course:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Delete Failed',
        message: 'Unable to remove the course. Please try again.',
      });
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setSelectedCourse(null);
    }
  };

  const menuGroups = useMemo(() => {
    if (!activeCourseForMenu) return [];
    const studentActions = [
      { label: 'View Students', onClick: () => handleViewStudents(activeCourseForMenu) },
      { label: 'Assign Student', onClick: () => handleAssignStudent(activeCourseForMenu) },
      { label: 'Remove Student', onClick: () => handleRemoveStudent(activeCourseForMenu) },
      { label: 'Bulk Assign Students', onClick: () => handleBulkAssign(activeCourseForMenu) },
    ];
    const courseActions = [
      { label: 'Edit Course Information', onClick: () => handleEditCourse(activeCourseForMenu) },
      { label: 'Manage Content', onClick: () => handleManageContent(activeCourseForMenu) },
      { label: 'Course Access Settings', onClick: () => handleVisibilitySettings(activeCourseForMenu) },
      { label: 'Student Access Settings', onClick: () => handleStudentVisibilitySettings(activeCourseForMenu) },
    ];
    if (user?.role === 'admin' || user?.role === 'instructor') {
      courseActions.push({
        label: 'Delete Course',
        onClick: () => handleDeleteCourse(activeCourseForMenu),
        destructive: true,
      });
    }
    return [
      { title: 'Students', actions: studentActions },
      { title: 'Course', actions: courseActions },
    ];
  }, [activeCourseForMenu, user, handleViewStudents, handleManageContent, handleVisibilitySettings, handleStudentVisibilitySettings]);

  const actionMenu = menuAnchor && activeCourseForMenu ? (
    <>
      <div className={styles.menuBackdrop} />
      <div ref={menuRef} className={styles.menuPopover} style={{ top: menuAnchor.top, left: menuAnchor.left }}>
        {menuGroups.map((group, index) => (
          <React.Fragment key={group.title}>
            <div className={styles.menuGroup}>
              <div className={styles.menuGroupTitle}>{group.title}</div>
              {group.actions.map(action => (
                <button
                  key={action.label}
                  type="button"
                  className={`${styles.menuItem} ${action.destructive ? styles.menuItemDestructive : ''}`}
                  onClick={() => handleMenuSelection(action.onClick)}
                >
                  {action.label}
                </button>
              ))}
            </div>
            {index < menuGroups.length - 1 && <div className={styles.menuDivider} />}
          </React.Fragment>
        ))}
      </div>
    </>
  ) : null;

  const filterButtons = filterMeta.map(filter => (
    <button
      key={filter.key}
      type="button"
      className={`${styles.filterButton} ${activeFilter === filter.key ? styles.filterButtonActive : ''}`}
      onClick={() => setActiveFilter(filter.key)}
    >
      {filter.label}
      <span className={styles.filterCount}>{filterCounts[filter.key] ?? 0}</span>
    </button>
  ));

  const statusClassMap = {
    Active: styles.statusBadgeActive,
    Inactive: styles.statusBadgeInactive,
    Ended: styles.statusBadgeEnded,
    'Not Started': styles.statusBadgeNotStarted,
  };

  const renderCourseCard = (course) => {
    const instructorName = `${course.instructor_first_name || ''} ${course.instructor_last_name || ''}`.trim();
    const isDescriptionLong = Boolean(course.description && course.description.length > DESCRIPTION_MIN_LENGTH);
    const isDescriptionExpanded = expandedDescriptions[course.id];
    const statusText = getCourseStatus(course);
    const statusClasses = `${styles.statusBadge} ${statusClassMap[statusText] ?? styles.statusBadgeNotStarted}`;
    return (
      <div key={course.id} className={styles.courseCard}>
        <div className={styles.cardTopRow}>
          <div className={styles.codeStatus}>
            <span className={styles.courseCode}>{course.code}</span>
            <span className={statusClasses}>
              {statusText}
            </span>
          </div>
          <button type="button" className={styles.manageButton} onClick={(event) => handleManageClick(event, course)}>
            Manage
          </button>
        </div>
        <h3 className={styles.courseTitle}>{course.name}</h3>
        <div className={styles.metaLine}>
          <span className={styles.metaText}>{course.code}</span>
          <span className={styles.metaDot}>•</span>
          <span className={styles.metaText}>{instructorName || 'TBD'}</span>
        </div>
        <div className={styles.dateRow}>
          <span className={styles.dateChip}>
            <FiCalendar className={styles.dateChipIcon} />
            {formatCourseDate(course)}
          </span>
        </div>
        <p className={`${styles.description} ${isDescriptionExpanded ? styles.descriptionExpanded : ''}`}>
          {course.description || 'No description available.'}
        </p>
        {isDescriptionLong && (
          <button
            type="button"
            className={styles.readMoreButton}
            onClick={() => expandDescription(course.id)}
          >
            {isDescriptionExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.pageContent}>
          <div className={styles.emptyState}>
            <FiBookOpen size={32} />
            <p>Loading courses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageContent}>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>Course Management</h1>
          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </div>
        <div className={styles.filterRow}>
          <div className={styles.filters}>{filterButtons}</div>
          {user?.role === 'admin' && (
            <button type="button" className={styles.addCourseButton} onClick={handleAddCourse}>
              <FiPlus />
              Create Course
            </button>
          )}
        </div>
        {filteredCourses.length > 0 ? (
          <div className={styles.grid}>
            {filteredCourses.map(renderCourseCard)}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <FiBookOpen size={48} />
            <h3>No courses found</h3>
            <p>{searchTerm ? `No courses match "${searchTerm}"` : 'There are no courses yet.'}</p>
          </div>
        )}
      </div>
      {actionMenu}
      <AddCourseModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onCourseAdded={(course) => setCourses(prev => [course, ...prev])} />
      <EditCourseModal
        isOpen={showEditModal}
        course={selectedCourse}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCourse(null);
        }}
        onCourseUpdated={(updatedCourse) => {
          setCourses(prev => prev.map(course => (course.id === updatedCourse.id ? updatedCourse : course)));
        }}
      />
      <AssignStudentModal
        isOpen={showAssignModal}
        course={selectedCourse}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedCourse(null);
        }}
        onStudentAssigned={(student) => {
          fetchCourses();
          setNotification({
            isOpen: true,
            type: 'success',
            title: 'Student Assigned',
            message: `${student.first_name} ${student.last_name} assigned.`,
          });
        }}
      />
      <BulkAssignModal
        isOpen={showBulkAssignModal}
        course={selectedCourse}
        onClose={() => {
          setShowBulkAssignModal(false);
          setSelectedCourse(null);
        }}
        onStudentsAssigned={() => {
          fetchCourses();
          setNotification({
            isOpen: true,
            type: 'success',
            title: 'Students Assigned',
            message: 'Students assigned successfully.',
          });
        }}
      />
      <RemoveStudentModal
        isOpen={showRemoveModal}
        course={selectedCourse}
        onClose={() => {
          setShowRemoveModal(false);
          setSelectedCourse(null);
        }}
        onStudentRemoved={() => {
          fetchCourses();
          setNotification({
            isOpen: true,
            type: 'success',
            title: 'Student Removed',
            message: 'Student removed successfully.',
          });
        }}
      />
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCourse(null);
        }}
        onConfirm={confirmDeleteCourse}
        item={selectedCourse}
        itemType="course"
        loading={deleteLoading}
      />
      {showVisibilityModal && selectedCourse && (
        <CourseVisibilityManager
          courseId={selectedCourse.id}
          courseName={selectedCourse.name}
          onClose={() => {
            setShowVisibilityModal(false);
            setSelectedCourse(null);
          }}
        />
      )}
      {showStudentVisibilityModal && selectedCourse && (
        <StudentVisibilityManager
          courseId={selectedCourse.id}
          courseName={selectedCourse.name}
          onClose={() => {
            setShowStudentVisibilityModal(false);
            setSelectedCourse(null);
          }}
        />
      )}
      <Notification
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}

export default CourseManagement;
